import * as React from "react";
import axios from "axios";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";
import { Link } from "react-router-dom";
// components
import Popover from "@material-ui/core/Popover";
import Icon from "common/icons";
import StickyBottom from "./stickyBottom";
import ProfileDefaultHeader from "../../components/profileDefaultHeader";
import TopInterestsList from "./topInterestsList";
import AllInterestsList from "./allInterestsList";
import SolidButton from "common/components/solidButton";
import ChangeTopInterestDialog from "../../components/changeTopInterestDialog";
import PageUpdater from "@vingle/wtf-sdk/components/pageUpdater";
import { VingleURL } from "@vingle/wtf-sdk/url";
// styles

const styles = require("./interests.scss");

interface IInterestsComponentProps {
  currentUser: Vingle.User.ICurrentUser | null;
  user?: Vingle.User.IUser;
  userProfile: Vingle.UserProfile.IProfileState;
  canEdit: boolean;
  onBackToInterestsPage: () => void;
  sortOutInterests: {
    topInterests: Vingle.UserProfile.IProfileInterestState[];
    notTopInterests: Vingle.UserProfile.IProfileInterestState[];
  };
  onChangeTopInterestOrder: (oldIndex: number, newIndex: number) => void;
  onBoost: (interest: Vingle.UserProfile.IProfileInterestState) => void;
  onInterestToNormal: (
    interest: Vingle.UserProfile.IProfileInterestState,
  ) => void;
  openInterestPopoverMenu: (
    interest: Vingle.UserProfile.IProfileInterestState,
    targetAnchor: HTMLElement,
  ) => void;
  onInterestToTop: (interest: Vingle.UserProfile.IProfileInterestState) => void;
  closeInterestPopoverMenu: () => void;
  onToggleInterestPrivate: () => void;
  onDeleteInterest: () => void;
  onEditDone: () => void;
  onTopInterestsDialogCloseRequest: () => void;
  onTopInterestsDialogSelectChange: (
    interest: Vingle.UserProfile.IProfileInterestState,
  ) => void;
  onTopInterestsDialogRequestSubmit: (
    username: string,
    interestToRemove: Vingle.UserProfile.IProfileInterestState,
  ) => Promise<void>;
}

class InterestsComponent extends React.PureComponent<
  IInterestsComponentProps & WrappedComponentProps
> {
  private readonly cancelTokenSource = axios.CancelToken.source();
  public componentWillUnmount() {
    this.cancelTokenSource.cancel();
  }
  public render() {
    const {
      intl,
      currentUser,
      userProfile,
      user,
      canEdit,
      onBackToInterestsPage,
      sortOutInterests,
      onChangeTopInterestOrder,
      onBoost,
      onInterestToNormal,
      openInterestPopoverMenu,
      onInterestToTop,
      closeInterestPopoverMenu,
      onToggleInterestPrivate,
      onDeleteInterest,
      onEditDone,
      onTopInterestsDialogCloseRequest,
      onTopInterestsDialogSelectChange,
      onTopInterestsDialogRequestSubmit,
    } = this.props;

    if (!user || !userProfile.interests.interests) {
      return null;
    }

    let isMine = false;
    if (user && currentUser) {
      isMine = user.id === currentUser.id;
    }

    if (!isMine && canEdit) {
      onBackToInterestsPage();
    }

    const { topInterests, notTopInterests } = sortOutInterests;

    let pageTitle = intl.formatMessage(
      { id: "interests.page_title" },
      { interests: intl.formatNumber(userProfile.interests.interests.length) },
    );
    if (canEdit) {
      pageTitle = intl.formatMessage({ id: "interests.page_title_edit" });
    }

    let menuPrivateText = intl.formatMessage({ id: "interests.menu_private" });
    if (
      userProfile.interests.meta.selectedNormalInterest &&
      userProfile.interests.meta.selectedNormalInterest.data &&
      userProfile.interests.meta.selectedNormalInterest.data.private
    ) {
      menuPrivateText = intl.formatMessage({ id: "interests.menu_public" });
    }

    return (
      <div>
        <PageUpdater
          title={`${user.username} ${canEdit ? " - Edit Interests" : ""}`}
        />
        <div className={styles.headerWrapper}>
          <ProfileDefaultHeader />
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.pageTitleWrapper}>
            <div className={styles.pageTitle}>{pageTitle}</div>
            {!canEdit && isMine ? (
              <Link
                to={new VingleURL.UserShowEditInterests({
                  username: user.username,
                }).toString()}
                className={styles.editWrapper}
              >
                <Icon icon="PROFILE_SETTING_SMALL" />
                <FormattedMessage id="interests.edit_text" />
              </Link>
            ) : null}
          </div>
          <TopInterestsList
            isLoading={userProfile.interests.meta.isLoading}
            listTitle={intl.formatMessage({
              id: "interests.top_interests_text",
            })}
            interests={topInterests}
            isMine={isMine}
            username={user ? user.username : ""}
            canEdit={canEdit}
            onChangeOrder={onChangeTopInterestOrder}
            onBoostClick={onBoost}
            onMainInterestClick={onInterestToNormal}
          />
          <AllInterestsList
            listTitle={intl.formatMessage({
              id: "interests.all_interests_text",
            })}
            interests={notTopInterests}
            isMine={isMine}
            canEdit={canEdit}
            username={user ? user.username : ""}
            handleBoostClick={onBoost}
            handleMenuClick={openInterestPopoverMenu}
            handleMainInterestClick={onInterestToTop}
          />
          <Popover
            classes={{ paper: styles.menuContainer }}
            open={userProfile.interests.meta.isOpenPopoverMenu}
            anchorEl={userProfile.interests.meta.selectedMenuAnchor}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            transformOrigin={{ horizontal: "center", vertical: "top" }}
            onClose={closeInterestPopoverMenu}
            elevation={2}
            transitionDuration={0}
          >
            <div className={styles.menuWrapper}>
              <div
                className={styles.menuItem}
                onClick={onToggleInterestPrivate}
              >
                {menuPrivateText}
              </div>
              <div
                className={`${styles.menuItem} ${styles.redText}`}
                onClick={onDeleteInterest}
              >
                {intl.formatMessage({ id: "interests.menu_delete" })}
              </div>
            </div>
          </Popover>
        </div>
        <StickyBottom show={canEdit} className={styles.bottom}>
          <SolidButton
            buttonText={intl.formatMessage({ id: "interests.edit_done" })}
            disabled={false}
            className={styles.doneButton}
            buttonStyle={{
              backgroundColor: "rgba(0,0,0,0)",
            }}
            onClick={onEditDone}
          />
        </StickyBottom>
        <ChangeTopInterestDialog
          topInterests={topInterests}
          selectedInterest={
            userProfile.interests.meta.selectedTopInterestInDialog
          }
          currentUser={currentUser}
          isOpen={userProfile.interests.meta.isChangeTopInterestDialogOpen}
          onRequestClose={onTopInterestsDialogCloseRequest}
          onSelectChange={onTopInterestsDialogSelectChange}
          onRequestSubmit={onTopInterestsDialogRequestSubmit}
        />
      </div>
    );
  }
}

export default injectIntl(InterestsComponent);
